import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ANNOUNCEMENTS = [
  { bold: '✅ 100% Authentic Products',              normal: 'Every item is genuinely verified and quality-checked before dispatch.',                    promo: null,        special: false },
  { bold: '🏙️ Only in Cairo',                       normal: 'Order before 10am & Get your Order At Your Door On The Same Day.',                         promo: 'T&C Apply',  special: false },
  { bold: '🎁 Welcome to the Family!',               normal: 'Grab 10% off your very first order — just for showing up 😍',                              promo: 'WELCOME10',  special: false },
  { bold: '⚡ SUPER FAST DELIVERY',                  normal: '1 ~ 2 Working Days — straight to your door.',                                              promo: null,        special: false },
  { bold: '🤍 More Love, More Savings',              normal: 'Your second order gets even sweeter — 15% off just for coming back ✨',                    promo: 'WELCOME15',  special: false },
  { bold: '🚀 Free Shipping? Yes Please!',           normal: 'Use this on any order and we\'ll cover your delivery, on us 📦',                           promo: 'FREESHIP',   special: false },
  { bold: '💎 The Rewards Program',                  normal: 'Your Loyalty, Rewarded. Forever.',                                                         promo: null,        special: false },
  { bold: '👨‍👩‍👧 Built for Families',              normal: 'A little loyalty goes a long way — here\'s 10% off because family matters 💛',             promo: 'FAMILY10',   special: false },
  { bold: '🏆 You\'re a ZIKO Legend',                normal: 'Top-tier loyalty deserves top-tier perks — 15% off because you\'ve earned it 🙌',          promo: 'LOYALTY15',  special: false },
  { bold: '⚡ SUPER FAST DELIVERY',                  normal: '1 ~ 2 Working Days — straight to your door.',                                              promo: null,        special: false },
  { bold: '🚀 Official Opening · 25 March 2026',     normal: 'STAY TUNED',                                                                               promo: null,        special: true  },
];

// Double the track — CSS animates by -50% for a seamless loop
const TRACK       = [...ANNOUNCEMENTS, ...ANNOUNCEMENTS];
const SESSION_KEY = 'ziko_announcement_dismissed';
const DURATION    = 90; // seconds

function LaunchText({ bold, normal }: { bold: string; normal: string }) {
  return (
    <span className="flex items-center gap-2">
      <span className="font-bold" style={{ background: 'linear-gradient(90deg, hsl(45 90% 60%), hsl(35 100% 70%), hsl(45 90% 60%))', backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'shimmerGold 2.4s linear infinite' }}>
        {bold}
      </span>
      <span className="font-black tracking-[0.18em] text-[11px] uppercase" style={{ background: 'linear-gradient(90deg, hsl(45 90% 55%), hsl(35 100% 65%), hsl(45 90% 55%))', backgroundSize: '200% auto', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', animation: 'shimmerGold 1.8s linear infinite', animationDelay: '0.3s' }}>
        {normal}
      </span>
    </span>
  );
}

export default function AnnouncementBar() {
  const [visible, setVisible] = useState(false);
  const trackRef   = useRef<HTMLDivElement>(null);
  const pausedAtX  = useRef<number | null>(null);
  const touchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { i18n }   = useTranslation();
  const isRtl      = i18n.language === 'ar';

  useEffect(() => {
    if (!sessionStorage.getItem(SESSION_KEY)) setVisible(true);
  }, []);

  const pause = () => {
    const el = trackRef.current;
    if (!el || pausedAtX.current !== null) return;
    const matrix = window.getComputedStyle(el).transform;
    let x = 0;
    if (matrix && matrix !== 'none') x = new DOMMatrix(matrix).m41;
    pausedAtX.current = x;
    el.style.animation = 'none';
    el.style.transform = `translateX(${x}px)`;
  };

  const resume = () => {
    const el = trackRef.current;
    if (!el || pausedAtX.current === null) return;
    const totalW   = el.scrollWidth / 2;
    let x          = pausedAtX.current % -totalW;
    if (x > 0) x  -= totalW;
    const elapsed  = (Math.abs(x) / totalW) * DURATION;
    el.style.transform = '';
    el.style.animation = `news-scroll ${DURATION}s linear -${elapsed.toFixed(4)}s infinite`;
    pausedAtX.current  = null;
  };

  const dismiss = () => { sessionStorage.setItem(SESSION_KEY, '1'); setVisible(false); };

  if (!visible) return null;

  return (
    <>
      <style>{`
        @keyframes news-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(${isRtl ? '50%' : '-50%'}); }
        }
        @keyframes shimmerGold {
          0%   { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
      `}</style>

      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
        className="relative overflow-hidden mt-2 border-b border-white/5"
        style={{ background: 'hsl(220 18% 7% / 0.82)', backdropFilter: 'blur(12px)' }}
        onMouseEnter={pause}
        onMouseLeave={resume}
        onTouchStart={() => {
          pause();
          if (touchTimer.current) clearTimeout(touchTimer.current);
          touchTimer.current = setTimeout(resume, 2000);
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

        <div className="relative flex items-center h-10 overflow-hidden">
          <div
            ref={trackRef}
            className="flex items-center whitespace-nowrap will-change-transform"
            style={{ animation: `news-scroll ${DURATION}s linear 0s infinite` }}
          >
            {TRACK.map((item, i) => (
              <span key={i} className="flex items-center gap-1.5 text-xs">
                {item.special ? (
                  <LaunchText bold={item.bold} normal={item.normal} />
                ) : (
                  <>
                    <span className="font-bold text-foreground">{item.bold}</span>
                    <span className="text-muted-foreground/60 mx-1">—</span>
                    <span className="text-muted-foreground">{item.normal}</span>
                    {item.promo && (
                      <>
                        <span className="text-muted-foreground/40 mx-1">·</span>
                        <span className="font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full text-[10px] border border-primary/20">{item.promo}</span>
                      </>
                    )}
                  </>
                )}
                <span className="text-primary/70 mx-8 text-sm">✦</span>
              </span>
            ))}
          </div>

          {/* Edge fades */}
          <div className="absolute left-0 top-0 bottom-0 w-14 bg-gradient-to-r from-[hsl(220_18%_7%/0.95)] to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 flex items-center z-10">
            <div className="w-14 bg-gradient-to-l from-[hsl(220_18%_7%/0.95)] to-transparent h-full pointer-events-none" />
            <button onClick={dismiss}
              className="flex items-center justify-center h-10 w-9 text-muted-foreground/50 hover:text-foreground transition-colors backdrop-blur-sm flex-shrink-0"
              aria-label="Dismiss">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </motion.div>
    </>
  );
}


